import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import SaveOutlined from '@ant-design/icons/SaveOutlined'
import {
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  message,
  Pagination,
  Progress,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd'
import { useRolodexEdit } from 'features/fde'
import html2canvas from 'html2canvas-pro'
import { useInspectionScanFiles } from 'pages/reporting/reports/raut/hooks/useInspectionScanFiles'
import {
  WeldIndication,
  WeldScanGeometry,
} from 'pages/reporting/reports/raut/hooks/useWeldIndications'
import { useWeldScanFiles } from 'pages/reporting/reports/raut/hooks/useWeldScanFiles'
import { WeldScanData } from 'pages/reporting/reports/raut/rautReportData'
import { getIndicationKey } from 'pages/reporting/reports/raut/utils/indicationUtils'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { SavedWeldIndicationGraph } from 'pages/reporting/shared/types'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { WeldIndicationDisplay } from './WeldIndicationDisplay'

const { Text } = Typography
const { Panel } = Collapse

/**
 * Extracts scan range from weld scan geometry based on the scan axis.
 * Since scans are done in a straight line, one axis will be fixed and the other varies.
 * Assumes geometry values are always in meters.
 *
 * @param geometry - Weld scan geometry with x and y ranges (in meters)
 * @param targetUnit - The unit to convert the values to
 * @returns The start and stop values for the scan range in the target unit
 */
const extractScanRange = (
  geometry: WeldScanGeometry | null | undefined,
  targetUnit: GeckoMeasurementUnit
): { start: number | undefined; stop: number | undefined } => {
  if (!geometry) {
    return { start: undefined, stop: undefined }
  }

  // If X is fixed, the scan moves along Y-axis
  let start: number, stop: number
  if (geometry.x_start === geometry.x_end) {
    start = geometry.y_start
    stop = geometry.y_end
  } else {
    start = geometry.x_start
    stop = geometry.x_end
  }
  const startMeasurement = GeckoMeasurement.CreateFromValue(
    start,
    GeckoMeasurementUnit.Meters
  )
  const stopMeasurement = GeckoMeasurement.CreateFromValue(
    stop,
    GeckoMeasurementUnit.Meters
  )

  return {
    start: Math.round(startMeasurement.convertTo(targetUnit).num * 100) / 100,
    stop: Math.round(stopMeasurement.convertTo(targetUnit).num * 100) / 100,
  }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const StatusCard = styled(Card)`
  margin-bottom: 1rem;
`

const WeldScanCard = styled(Card)`
  margin-bottom: 1.5rem;
`

const FormSection = styled.div`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`

const IndicationCardStyled = styled(Card)`
  margin-bottom: 1rem;
`

const PaginationContainer = styled.div`
  margin-top: 1rem;
  text-align: center;
`

const ActionButtonGroup = styled(Space)`
  margin-top: 0.625rem;
  display: flex;
  justify-content: flex-end;
`

/**
 * Information about a weld scan file and its associated indications
 */
interface WeldScanFileInfo {
  fileName: string
  indications: WeldIndication[]
  scanStart: number | undefined
  scanStop: number | undefined
  unit: string
}

/**
 * Form data for a single weld scan (user-editable fields)
 */
interface WeldScanFormData {
  fileName: string // Immutable identifier
  scanLocation: string
  scanLabel: string
  scanStart: number | undefined
  scanStop: number | undefined
  centerlineNeg: number | undefined
  centerlinePos: number | undefined
  totalIndications: number
  comments: string
}

interface WeldIndicationCardProps {
  indication: WeldIndication
  metadataEntryId: string
  isSaved: boolean
  onSave: (
    indicationId: string,
    imagePath: string
  ) => Promise<Record<string, SavedWeldIndicationGraph>>
  onDelete: (
    indicationId: string
  ) => Promise<Record<string, SavedWeldIndicationGraph>>
}

/**
 * Individual card for displaying and saving a weld indication visualization
 */
const WeldIndicationCard: FC<WeldIndicationCardProps> = ({
  indication,
  metadataEntryId,
  isSaved,
  onSave,
  onDelete,
}) => {
  const { scanFiles, isLoading, hasScanData } = useWeldScanFiles({
    id: indication.id,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const graphsRef = useRef<HTMLDivElement>(null)
  const { rolodexUploadFile, rolodexDeleteFile } = useRolodexEdit()

  const handleSave = useCallback(async () => {
    if (!graphsRef.current) return

    try {
      setIsSaving(true)

      message.loading({
        content: 'Capturing visualization...',
        key: 'weld-save',
        duration: 0,
      })

      // Capture the visualization as an image
      const canvas = await html2canvas(graphsRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        removeContainer: true,
        allowTaint: false,
        foreignObjectRendering: false,
      })

      // Convert to blob
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob(
          (b) => resolve(b || new Blob([], { type: 'image/png' })),
          'image/png',
          0.9
        )
      })

      // Upload to Rolodex
      const imagePath = `weld-indications/${indication.id}/visualization.png`
      await rolodexUploadFile(metadataEntryId, blob, imagePath)

      await onSave(indication.id, imagePath)

      message.success({
        content: 'Visualization saved successfully',
        key: 'weld-save',
      })
    } catch (error) {
      console.error('Error saving weld indication visualization:', error)
      message.error({
        content: 'Failed to save visualization',
        key: 'weld-save',
      })
    } finally {
      setIsSaving(false)
    }
  }, [indication.id, metadataEntryId, onSave, rolodexUploadFile])

  const handleDelete = useCallback(async () => {
    try {
      setIsDeleting(true)

      // Delete the saved image
      const imagePath = `weld-indications/${indication.id}/visualization.png`
      await rolodexDeleteFile(metadataEntryId, imagePath)

      await onDelete(indication.id)

      message.success('Visualization deleted successfully')
    } catch (error) {
      console.error('Error deleting weld indication visualization:', error)
      message.error('Failed to delete visualization')
    } finally {
      setIsDeleting(false)
    }
  }, [indication.id, metadataEntryId, onDelete, rolodexDeleteFile])

  if (isLoading) {
    return (
      <IndicationCardStyled title={indication.title}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <Text>Loading scan data...</Text>
          <Progress status='active' percent={50} />
        </Space>
      </IndicationCardStyled>
    )
  }

  return (
    <IndicationCardStyled
      title={
        <Space>
          <span>{indication.title}</span>
          {isSaved && (
            <Tooltip title='Visualization saved'>
              <Text type='success'>✓ Saved</Text>
            </Tooltip>
          )}
        </Space>
      }
      extra={
        <ActionButtonGroup>
          <Button
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={isSaving}
            disabled={!hasScanData || isSaving}
            type={isSaved ? 'default' : 'primary'}>
            {isSaved ? 'Update' : 'Save'}
          </Button>
          {isSaved && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              loading={isDeleting}
              disabled={isDeleting}>
              Delete
            </Button>
          )}
        </ActionButtonGroup>
      }>
      <WeldIndicationDisplay
        scanFiles={scanFiles}
        indication={indication}
        graphsRef={graphsRef}
        isLoading={isLoading}
      />
    </IndicationCardStyled>
  )
}

interface FileScanVisualizationCardProps {
  inspectionId: string | undefined
  fileName: string
  metadataEntryId: string
  isSaved: boolean
  onSave: (
    fileName: string,
    imagePath: string
  ) => Promise<Record<string, SavedWeldIndicationGraph>>
  onDelete: (
    fileName: string
  ) => Promise<Record<string, SavedWeldIndicationGraph>>
}

/**
 * Card for displaying and saving file scan visualizations (files without indications)
 */
const FileScanVisualizationCard: FC<FileScanVisualizationCardProps> = ({
  inspectionId,
  fileName,
  metadataEntryId,
  isSaved,
  onSave,
  onDelete,
}) => {
  const { scanFiles, isLoading, hasScanData } = useInspectionScanFiles({
    inspectionId,
    fileName,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const graphsRef = useRef<HTMLDivElement>(null)
  const { rolodexUploadFile, rolodexDeleteFile } = useRolodexEdit()

  const handleSave = useCallback(async () => {
    if (!graphsRef.current) return

    try {
      setIsSaving(true)

      message.loading({
        content: 'Capturing visualization...',
        key: 'file-scan-save',
        duration: 0,
      })

      const canvas = await html2canvas(graphsRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        removeContainer: true,
        allowTaint: false,
        foreignObjectRendering: false,
      })

      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob(
          (b) => resolve(b || new Blob([], { type: 'image/png' })),
          'image/png',
          0.9
        )
      })

      // Upload to Rolodex - remove .nde extension
      const cleanFileName = fileName.replace(/\.nde$/i, '')
      const imagePath = `weld-indications/${cleanFileName}/visualization.png`
      await rolodexUploadFile(metadataEntryId, blob, imagePath)

      await onSave(fileName, imagePath)

      message.success({
        content: 'Visualization saved successfully',
        key: 'file-scan-save',
      })
    } catch (error) {
      console.error('Error saving file scan visualization:', error)
      message.error({
        content: 'Failed to save visualization',
        key: 'file-scan-save',
      })
    } finally {
      setIsSaving(false)
    }
  }, [fileName, metadataEntryId, onSave, rolodexUploadFile])

  const handleDelete = useCallback(async () => {
    try {
      setIsDeleting(true)

      // Delete the saved image - remove .nde extension
      const cleanFileName = fileName.replace(/\.nde$/i, '')
      const imagePath = `weld-indications/${cleanFileName}/visualization.png`
      await rolodexDeleteFile(metadataEntryId, imagePath)

      await onDelete(fileName)

      message.success('Visualization deleted successfully')
    } catch (error) {
      console.error('Error deleting file scan visualization:', error)
      message.error('Failed to delete visualization')
    } finally {
      setIsDeleting(false)
    }
  }, [fileName, metadataEntryId, onDelete, rolodexDeleteFile])

  if (!isLoading && !hasScanData) {
    return null
  }

  if (isLoading) {
    return (
      <IndicationCardStyled title='Scan Visualization'>
        <Space direction='vertical' style={{ width: '100%' }}>
          <Text>Loading scan data...</Text>
          <Progress status='active' percent={50} />
        </Space>
      </IndicationCardStyled>
    )
  }

  return (
    <IndicationCardStyled
      title={
        <Space>
          <span>Scan Visualization</span>
          {isSaved && (
            <Tooltip title='Visualization saved'>
              <Text type='success'>✓ Saved</Text>
            </Tooltip>
          )}
        </Space>
      }
      extra={
        <ActionButtonGroup>
          <Button
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={isSaving}
            disabled={!hasScanData || isSaving}
            type={isSaved ? 'default' : 'primary'}>
            {isSaved ? 'Update' : 'Save'}
          </Button>
          {isSaved && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              loading={isDeleting}
              disabled={isDeleting}>
              Delete
            </Button>
          )}
        </ActionButtonGroup>
      }>
      <WeldIndicationDisplay
        scanFiles={scanFiles}
        fileName={fileName}
        graphsRef={graphsRef}
        isLoading={isLoading}
      />
    </IndicationCardStyled>
  )
}

interface FileGroupSectionProps {
  group: WeldScanFileInfo
  field: { name: number; key: number }
  inspectionId: string | undefined
  metadataEntryId: string
  savedWeldIndicationIds: Set<string>
  onSaveIndication: (
    indicationId: string,
    imagePath: string
  ) => Promise<Record<string, SavedWeldIndicationGraph>>
  onDeleteIndication: (
    indicationId: string
  ) => Promise<Record<string, SavedWeldIndicationGraph>>
  geometricUnit: GeckoMeasurementUnit
}

/**
 * Section for a single file group with form fields and indication cards
 */
const FileGroupSection: FC<FileGroupSectionProps> = ({
  group,
  field,
  inspectionId,
  metadataEntryId,
  savedWeldIndicationIds,
  onSaveIndication,
  onDeleteIndication,
  geometricUnit,
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 1

  // Calculate progress for this group
  const savedCount = group.indications.filter((ind) =>
    savedWeldIndicationIds.has(getIndicationKey(ind.id))
  ).length
  const progressPercentage =
    group.indications.length > 0
      ? Math.floor((savedCount / group.indications.length) * 100)
      : 0

  // Paginate indications within this group
  const startIndex = (currentPage - 1) * pageSize
  const paginatedIndications = group.indications.slice(
    startIndex,
    startIndex + pageSize
  )

  const unitStr =
    geometricUnit === GeckoMeasurementUnit.Millimeters
      ? GeckoMeasurementUnit.Millimeters.id
      : GeckoMeasurementUnit.Inches.id

  return (
    <WeldScanCard
      title={`Scan: ${group.fileName || 'Unknown'}`}
      extra={
        <Text type='secondary'>
          {savedCount} of {group.indications.length} visualizations saved
        </Text>
      }>
      {/* Form Fields */}
      <FormSection>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Form.Item
              name={[field.name, 'scanLocation']}
              label='Scan Location'
              style={{ marginBottom: 8 }}>
              <Input placeholder='e.g., CW-1, LS-1' />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={[field.name, 'scanLabel']}
              label='Scan Label'
              style={{ marginBottom: 8 }}>
              <Input placeholder='ex. S01, S02, etc.' />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={[field.name, 'totalIndications']}
              label='Total Indications'
              style={{ marginBottom: 8 }}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Form.Item
              name={[field.name, 'scanStart']}
              label={`Scan Start (${unitStr})`}
              style={{ marginBottom: 8 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name={[field.name, 'scanStop']}
              label={`Scan Stop (${unitStr})`}
              style={{ marginBottom: 8 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name={[field.name, 'centerlineNeg']}
              label={`Centerline Neg (${unitStr})`}
              style={{ marginBottom: 8 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name={[field.name, 'centerlinePos']}
              label={`Centerline Pos (${unitStr})`}
              style={{ marginBottom: 8 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              name={[field.name, 'comments']}
              label='Comments'
              style={{ marginBottom: 0 }}>
              <Input.TextArea rows={2} placeholder='Additional comments...' />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>

      {/* Progress */}
      <div style={{ marginBottom: '1rem' }}>
        <Progress percent={progressPercentage} size='small' />
      </div>

      {group.indications.length === 0 && (
        <FileScanVisualizationCard
          inspectionId={inspectionId}
          fileName={group.fileName}
          metadataEntryId={metadataEntryId}
          isSaved={savedWeldIndicationIds.has(getIndicationKey(group.fileName))}
          onSave={onSaveIndication}
          onDelete={onDeleteIndication}
        />
      )}

      {/* Indication Cards - Only show if there are indications */}
      {group.indications.length > 0 ? (
        <Collapse defaultActiveKey={['indications']}>
          <Panel header='Indication Visualizations' key='indications'>
            {paginatedIndications.map((indication) => (
              <WeldIndicationCard
                key={indication.id}
                indication={indication}
                metadataEntryId={metadataEntryId}
                isSaved={savedWeldIndicationIds.has(
                  getIndicationKey(indication.id)
                )}
                onSave={onSaveIndication}
                onDelete={onDeleteIndication}
              />
            ))}

            {group.indications.length > 1 && (
              <PaginationContainer>
                <Pagination
                  current={currentPage}
                  onChange={setCurrentPage}
                  total={group.indications.length}
                  pageSize={pageSize}
                  showSizeChanger={false}
                  showQuickJumper
                />
              </PaginationContainer>
            )}
          </Panel>
        </Collapse>
      ) : (
        <div style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>
          <Text type='secondary'>
            No indications for this scan. Complete the metadata fields above.
          </Text>
        </div>
      )}
    </WeldScanCard>
  )
}

interface WeldIndicationsViewProps {
  weldIndications: WeldIndication[]
  allScanFileNames: string[]
  inspectionId: string | undefined
  metadataEntryId: string
  savedWeldIndicationIds: Set<string>
  onSaveIndication: (
    indicationId: string,
    imagePath: string
  ) => Promise<Record<string, SavedWeldIndicationGraph>>
  onDeleteIndication: (
    indicationId: string
  ) => Promise<Record<string, SavedWeldIndicationGraph>>
  weldScanData: WeldScanData | undefined
  onWeldScanDataFinish: (data: WeldScanData) => Promise<void>
}

/**
 * WeldIndicationsView - Displays weld indications grouped by file name
 *
 * Shows form fields for scan summary data and paginated indication visualizations
 * per file group. Auto-populates scan range from weld_scan_geometry when available.
 */
export const WeldIndicationsView: FC<WeldIndicationsViewProps> = ({
  weldIndications,
  allScanFileNames,
  inspectionId,
  metadataEntryId,
  savedWeldIndicationIds,
  onSaveIndication,
  onDeleteIndication,
  weldScanData,
  onWeldScanDataFinish,
}) => {
  const [form] = Form.useForm()
  const { getGeometricUnit } = useUnitSystem()
  const geometricUnit = getGeometricUnit()
  const [isSaving, setIsSaving] = useState(false)

  // Group indications by file_name and include ALL scan files
  const fileGroups = useMemo(() => {
    const groups: Map<string, WeldScanFileInfo> = new Map()

    // First, initialize groups for ALL scan files
    allScanFileNames.forEach((fileName) => {
      groups.set(fileName, {
        fileName,
        indications: [],
        scanStart: undefined,
        scanStop: undefined,
        unit: GeckoMeasurementUnit.Millimeters.id,
      })
    })

    // Then, add indications to their respective groups and populate geometry
    weldIndications.forEach((indication) => {
      const fileName = indication.customInfo.file_name || 'Unknown'

      // If this file wasn't in allScanFileNames, add it anyway (shouldn't happen)
      if (!groups.has(fileName)) {
        groups.set(fileName, {
          fileName,
          indications: [],
          scanStart: undefined,
          scanStop: undefined,
          unit: GeckoMeasurementUnit.Millimeters.id,
        })
      }

      const group = groups.get(fileName)!
      group.indications.push(indication)

      // Populate geometry from the first indication if not already set
      if (
        group.scanStart === undefined &&
        indication.customInfo.weld_scan_geometry
      ) {
        const geometry = indication.customInfo.weld_scan_geometry
        const { start, stop } = extractScanRange(geometry, geometricUnit)
        group.scanStart = start
        group.scanStop = stop
        group.unit = geometry.unit || GeckoMeasurementUnit.Millimeters.id
      }
    })

    // Sort indications within each group by indication number
    const extractIndicationNumber = (title: string): number => {
      const match = title.match(/Indication\s+(\d+)/i)
      return match ? parseInt(match[1], 10) : 0
    }

    groups.forEach((group) => {
      group.indications.sort((a, b) => {
        const aNum = extractIndicationNumber(a.title)
        const bNum = extractIndicationNumber(b.title)
        return aNum - bNum
      })
    })

    return Array.from(groups.values())
  }, [weldIndications, allScanFileNames, geometricUnit])

  // Compute initial values with priority: saved backend data || derived defaults
  const initialValues = useMemo(() => {
    const scans = fileGroups.map((group) => {
      const existingScan = weldScanData?.scans.find(
        (s) => s.fileName === group.fileName
      )

      return {
        fileName: group.fileName, // Immutable identifier
        scanLocation: existingScan?.scanLocation || '',
        scanLabel: existingScan?.scanLabel || '',
        scanStart:
          existingScan?.scanStart?.toNumber(geometricUnit) ?? group.scanStart,
        scanStop:
          existingScan?.scanStop?.toNumber(geometricUnit) ?? group.scanStop,
        centerlineNeg: existingScan?.centerlineNeg?.toNumber(geometricUnit),
        centerlinePos: existingScan?.centerlinePos?.toNumber(geometricUnit),
        totalIndications:
          existingScan?.totalIndications ?? group.indications.length,
        comments: existingScan?.comments || '',
      }
    })

    return { scans }
  }, [fileGroups, weldScanData, geometricUnit])

  // Set form values when initialValues change
  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form, initialValues])

  // Handle form submission
  const handleFinish = useCallback(
    async (values: { scans: WeldScanFormData[] }) => {
      try {
        setIsSaving(true)

        // Convert form values to WeldScanData
        const weldScanData: WeldScanData = {
          totalScans: values.scans.length,
          scans: values.scans.map((scan, index) => ({
            id: `scan-${index}`,
            fileName: scan.fileName, // Use the immutable fileName
            scanLocation: scan.scanLocation,
            scanLabel: scan.scanLabel || '',
            scanStart:
              scan.scanStart !== undefined
                ? GeckoMeasurement.CreateFromValue(
                    scan.scanStart,
                    geometricUnit
                  )
                : GeckoMeasurement.CreateFromValue(0, geometricUnit),
            scanStop:
              scan.scanStop !== undefined
                ? GeckoMeasurement.CreateFromValue(scan.scanStop, geometricUnit)
                : GeckoMeasurement.CreateFromValue(0, geometricUnit),
            centerlineNeg:
              scan.centerlineNeg !== undefined
                ? GeckoMeasurement.CreateFromValue(
                    scan.centerlineNeg,
                    geometricUnit
                  )
                : GeckoMeasurement.CreateFromValue(0, geometricUnit),
            centerlinePos:
              scan.centerlinePos !== undefined
                ? GeckoMeasurement.CreateFromValue(
                    scan.centerlinePos,
                    geometricUnit
                  )
                : GeckoMeasurement.CreateFromValue(0, geometricUnit),
            totalIndications: scan.totalIndications,
            comments: scan.comments || '',
            indications: [],
          })),
        }

        await onWeldScanDataFinish(weldScanData)
        message.success('Weld information saved successfully!')
      } catch (error) {
        console.error('Error saving weld scan data:', error)
        message.error('Failed to save weld information')
      } finally {
        setIsSaving(false)
      }
    },
    [geometricUnit, onWeldScanDataFinish]
  )

  // Calculate overall progress
  const totalScans = fileGroups.length
  const totalIndications = weldIndications.length

  // Count scans with complete metadata
  const scansWithCompleteMetadata = fileGroups.filter((group) => {
    const existingScan = weldScanData?.scans.find(
      (s) => s.fileName === group.fileName
    )
    if (!existingScan) return false

    // Check all required fields are filled
    return (
      existingScan.scanLocation &&
      existingScan.scanLabel &&
      existingScan.scanStart !== undefined &&
      existingScan.scanStop !== undefined &&
      existingScan.centerlineNeg !== undefined &&
      existingScan.centerlinePos !== undefined &&
      existingScan.totalIndications !== undefined
    )
  }).length

  // Count saved visualizations
  const savedVisualizationsCount = weldIndications.filter((ind) =>
    savedWeldIndicationIds.has(getIndicationKey(ind.id))
  ).length

  // Calculate percentages
  const scanMetadataPercentage =
    totalScans > 0
      ? Math.floor((scansWithCompleteMetadata / totalScans) * 100)
      : 100
  const visualizationsPercentage =
    totalIndications > 0
      ? Math.floor((savedVisualizationsCount / totalIndications) * 100)
      : 100
  const overallCompletedItems =
    scansWithCompleteMetadata + savedVisualizationsCount
  const overallTotalItems = totalScans + totalIndications
  const overallPercentage =
    overallTotalItems > 0
      ? Math.floor((overallCompletedItems / overallTotalItems) * 100)
      : 100

  return (
    <Container>
      <StatusCard>
        <Typography.Title level={5}>Overall Progress</Typography.Title>
        <div style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <Text strong>Scan Metadata: </Text>
            <Text>
              {scansWithCompleteMetadata} of {totalScans} scans completed (
              {scanMetadataPercentage}%)
            </Text>
          </div>
          <Progress
            percent={scanMetadataPercentage}
            size='small'
            status={scanMetadataPercentage === 100 ? 'success' : 'active'}
          />
        </div>
        <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <Text strong>Indication Visualizations: </Text>
            <Text>
              {savedVisualizationsCount} of {totalIndications} saved (
              {visualizationsPercentage}%)
            </Text>
          </div>
          <Progress
            percent={visualizationsPercentage}
            size='small'
            status={visualizationsPercentage === 100 ? 'success' : 'active'}
          />
        </div>
        <div style={{ marginTop: '1rem' }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <Text strong>Overall: </Text>
            <Text>
              {overallCompletedItems} of {overallTotalItems} items completed (
              {overallPercentage}%)
            </Text>
          </div>
          <Progress
            percent={overallPercentage}
            status={overallPercentage === 100 ? 'success' : 'active'}
          />
        </div>
      </StatusCard>

      <StyledForm
        form={form}
        layout='vertical'
        initialValues={initialValues}
        onFinish={handleFinish}>
        {/* Hidden field to track fileName for each scan */}
        <Form.List name='scans'>
          {(fields) => (
            <>
              {fields.map((field, index) => {
                const group = fileGroups[index]

                return (
                  <React.Fragment key={field.key}>
                    {/* Hidden field for fileName */}
                    <Form.Item name={[field.name, 'fileName']} hidden>
                      <Input />
                    </Form.Item>

                    <FileGroupSection
                      group={group}
                      field={field}
                      inspectionId={inspectionId}
                      metadataEntryId={metadataEntryId}
                      savedWeldIndicationIds={savedWeldIndicationIds}
                      onSaveIndication={onSaveIndication}
                      onDeleteIndication={onDeleteIndication}
                      geometricUnit={geometricUnit}
                    />
                  </React.Fragment>
                )
              })}
            </>
          )}
        </Form.List>

        <Form.Item>
          <Button
            type='primary'
            htmlType='submit'
            loading={isSaving}
            disabled={isSaving}>
            Save Weld Information
          </Button>
        </Form.Item>
      </StyledForm>
    </Container>
  )
}

export default WeldIndicationsView
